import { InsurerEdit } from './../model/insurerEdit';
import { Component, OnInit } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { InsurerService } from 'src/app/core/service/insurer.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  selector: 'app-insurer-list',
  templateUrl: './insurer-list.component.html',
  styleUrls: ['./insurer-list.component.scss']
})
export class InsurerListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: InsurerEdit;
  public selectedInsurer: InsurerEdit;
  public filterText = '';
  public title = '';
  public revenueManager = false;

  constructor(private insurerService: InsurerService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
    private authenticationService: AuthenticationService,)
  {
  }

  public ngOnInit(): void
  {
    this.revenueManager = this.authenticationService.currentUser.role === 'ROLE_REVENUE_MANAGER_OPERATOR';
    this.translate.get(['INSURERS', 'PAYERS']).subscribe({
      next: (res) =>
      {
        if (this.authenticationService.currentUser.role === 'ROLE_REVENUE_MANAGER_OPERATOR')
        {
          this.title = res.PAYERS;
        }
        else
        {
          this.title = res.INSURERS;
        }
      }
    });
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const insurerId = this.selectedInsurer.id;
      this.insurerService.deleteInsurer(insurerId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== insurerId);
          this.view.refreshView();
          this.count = this.view.data.length;
          if (this.revenueManager)
          {
            this.messageHandlerService.displaySuccess('PAYER-DELETED-SUCCESSFULLY');
          }
          else
          {
            this.messageHandlerService.displaySuccess('INSURER-DELETED-SUCCESSFULLY');
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedInsurer = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.insurerService.insurerInUse(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {          
          if (this.revenueManager)
          {
            this.messageHandlerService.displayWarning('PAYER-IN-USE-CANNOT-DELETE');
          }
          else
          {
            this.messageHandlerService.displayWarning('INSURER-IN-USE-CANNOT-DELETE');
          }          
        }
        else
        {
          this.insurerService.getInsurerById(id).subscribe({
            next: (data) =>
            {
              this.selectedInsurer = data as InsurerEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new InsurerEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.insurerService.getInsurerById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(insurer: InsurerEdit): void
  {
    this.insurerService.saveInsurer(insurer, this.isNew, insurer.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        if (data.feeSchedules !== null && data.feeSchedules.length > 0 && data.feeSchedules.map !== undefined)
        {
          data.feeSchedules = data.feeSchedules !== null ? data.feeSchedules.map(x => x.displayName).join(', ') : '';
        }
        else
        {
          data.feeSchedules = '';
        }
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        if (this.revenueManager)
        {
          this.messageHandlerService.displaySuccess('PAYER-SAVED-SUCCESSFULLY');
        }
        else
        {
          this.messageHandlerService.displaySuccess('INSURER-SAVED-SUCCESSFULLY');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.insurerService.getInsurerList().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.lastModifiedOn = new Date(d.lastModifiedOn);
          if (d.feeSchedules !== null && d.feeSchedules.length > 0 && d.feeSchedules.map !== undefined)
          {
            d.feeSchedules = d.feeSchedules !== null ? d.feeSchedules.map(x => x.displayName).join(', ') : '';
          }
          else
          {
            d.feeSchedules = '';
          }          
        });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
